// The shot chart's arithmetic: where a shot goes on the half-court, and what the chart says about the shots. P6-330b (GN-3b).
//
// Pure functions over the game blob's `shots` (`utils/hoops_projection.py`, "THE SHOTS" and "THE SHOT COORDINATES,
// MEASURED"): every field-goal attempt of the game, oldest first, each `{period, clock, team, person_id, x, y, made,
// points}`. The components draw what these return and nothing more.
//
// ═══════════════════════════════════════════════════════════════════════════
// TWO BASKETS, ONE HALF-COURT, AND A ROTATION, NOT A MIRROR
// ═══════════════════════════════════════════════════════════════════════════
//
// `x` and `y` are the NBA's own, as percentages of the WHOLE court: `x` along its LENGTH (0 is the left baseline and
// 100 the right one, the baskets at 5.6 and 94.4), `y` across its width (50 is the line through both baskets), and
// 100 units of `x` are 94 ft where 100 units of `y` are 50 ft. The two teams attack opposite baskets, swap at the
// half, and which team starts where is NOT fixed by home or away, so the basket is read from `x` and never from the
// team: a shot at `x > 50` is at the right basket.
//
// To draw both baskets on one half-court a right-basket shot is ROTATED through 180 degrees, `(x, y) -> (100 - x,
// 100 - y)`, as the producer's contract says: the feed's own shooter-relative figure (`xLegacy`) changes sign
// between the baskets exactly as that rotation does, so a shot stays on the shooter's left or right, which
// mirroring `x` alone would swap.
//
// ⚠️ WHAT THIS DOES NOT SETTLE, AND NOTHING IN THE RECORDING CAN: WHICH SIDE OF THE PICTURE IS THE SHOOTER'S
// RIGHT. The recorded play-by-play carries no zone words (`area`), only `x`, `y`, `side`, `xLegacy` and
// `shotDistance`, so the handedness of the picture is a choice, made here once: the court is drawn with the
// basket at the top, as the design draws it, and `xLegacy` (the feed's lateral figure, `250 - 5y` at the left
// basket) is the picture's x, `(50 - y) * 0.5` ft. Reversing it is a mirror of the whole picture; it moves no
// dot relative to another, no distance, no count and no made or missed, and it would swap a left corner for a
// right one, which the page never says in words.

/** A basket's place along the court, as a percentage of its length from the nearer baseline. */
export const BASKET_X = 5.6
/** 100 units of `x` are 94 ft along the court's length. */
export const FEET_PER_X = 0.94
/** 100 units of `y` are 50 ft across its width. */
export const FEET_PER_Y = 0.5
/** The half-court the chart draws, in feet: 50 wide, from 5.25 ft behind the basket to the half-court line. */
export const COURT = Object.freeze({ left: -25, top: -5.25, width: 50, height: 47 })

const isCoordinate = (value) => typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 100
const tenth = (value) => Math.round(value * 10) / 10
const hundredth = (value) => Math.round(value * 100) / 100

/**
 * A shot's place with every shot at the LEFT basket: the same percentages, the right basket's shots turned
 * through 180 degrees. `null` for a shot with no usable position. Rounded to a tenth, the feed's own precision, so
 * `100 - 94.4` is `5.6` and not `5.599999999999994`.
 */
export function foldToLeftBasket(x, y) {
  if (!isCoordinate(x) || !isCoordinate(y)) return null
  return x > 50 ? { x: tenth(100 - x), y: tenth(100 - y) } : { x, y }
}

/**
 * A shot's place on the chart, in feet from the basket: `x` across (the shooter's lateral figure) and `y` out from
 * the basket towards the half-court line, so a shot AT a basket is `(0, 0)` and the half-court line is `y` 41.75.
 * `null` for a shot with no usable position.
 */
export function courtPoint(x, y) {
  const folded = foldToLeftBasket(x, y)
  if (folded === null) return null
  return {
    x: hundredth((50 - folded.y) * FEET_PER_Y),
    y: hundredth((folded.x - BASKET_X) * FEET_PER_X),
  }
}

/** Which side of the game a team's tricode is, or `null`. */
export function sideOf(game, tricode) {
  if (typeof tricode !== 'string' || tricode === '') return null
  if (tricode === game?.away?.tricode) return 'away'
  if (tricode === game?.home?.tricode) return 'home'
  return null
}

/**
 * The chart for a game's shots.
 *
 * `dots` are the shots that have a position, oldest first, each with a KEY THAT SURVIVES THE LIST GROWING (the
 * same shot keeps the same key from one poll to the next, so a dot that is already on the court is not torn down
 * and drawn in again every fifteen seconds; two identical shots in the same second are told apart by their
 * occurrence, as `gameModel.js::playRows` does). `total` is EVERY shot, positioned or not: a shot the feed gave no
 * position for is still a shot, and `splits` count it, so a team's `made of attempted` is its field-goal line in
 * the box score and not a figure that depends on how many shots happened to be placed.
 */
export function shotChart(game) {
  const list = Array.isArray(game?.shots) ? game.shots : []
  const splits = { away: { made: 0, attempted: 0 }, home: { made: 0, attempted: 0 } }
  const seen = new Map()
  const dots = []
  let total = 0
  for (const shot of list) {
    if (shot === null || typeof shot !== 'object') continue
    total += 1
    const side = sideOf(game, shot.team)
    const made = shot.made === true
    if (side !== null) {
      splits[side].attempted += 1
      if (made) splits[side].made += 1
    }
    const point = courtPoint(shot.x, shot.y)
    if (point === null) continue
    const base = [shot.period, shot.clock, shot.team, shot.person_id, shot.x, shot.y, made].join('|')
    const n = (seen.get(base) ?? 0) + 1
    seen.set(base, n)
    dots.push({ key: `${base}|${n}`, cx: point.x, cy: point.y, made, side })
  }
  return { total, located: dots.length, unlocated: total - dots.length, dots, splits }
}

/** The dots to draw for a filter: `both`, `away` or `home`. A shot of a team that is neither side is only in `both`. */
export function visibleDots(chart, filter) {
  return filter === 'away' || filter === 'home' ? chart.dots.filter((dot) => dot.side === filter) : chart.dots
}

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`

/**
 * The note beside the chart's heading: what the chart covers and how many shots it holds.
 *
 * ⚠️ THE DESIGN'S WAS `Since Q2 2:10 · 34 shots`, and it was true of a page that held the last fifty plays. This
 * chart holds EVERY shot of the game, so it says that instead: `Whole game · 201 shots` for a game that is over,
 * `So far · 34 shots` for one still being played, and `· 2 not placed` when the feed gave a shot no position, so the
 * number is never larger than what is on the court without saying so.
 */
export function shotNote(chart, live) {
  const parts = [live ? 'So far' : 'Whole game', plural(chart.total, 'shot')]
  if (chart.unlocated > 0) parts.push(`${chart.unlocated} not placed`)
  return parts.join(' · ')
}

/** The chart's text alternative: what is drawn, in the design's own words. */
export function shotAria(chart) {
  return `Half court, ${plural(chart.located, 'shot')}: made filled, missed hollow.`
}

/** `HOU 41 of 90`: one team's field goals, for the line under the chart. */
export function splitText(tricode, split) {
  return `${tricode} ${split.made} of ${split.attempted}`
}
